/**
 * CouponHeaderPill — tiny % icon button that lives in page headers.
 * Tap → opens the active coupon barcode in a bottom sheet (keeps the
 * page header icons reachable so the user can still tap Home, unlike
 * the old full-screen modal which "overpowered the page" per user
 * report 2026-06-15). Hides itself when no active coupon exists.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Percent } from "@phosphor-icons/react";
import { BarcodeBottomSheet } from "./DailyPasscodeCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shortDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export default function CouponHeaderPill() {
  const [coupon, setCoupon] = useState(null);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/coupons/active`);
        if (!cancelled) setCoupon(r.data || null);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!coupon) return null;

  // Use the admin-typed label as the headline. Bug 2026-02-14: user
  // entered a custom label like "FREE PIZZA" but the popover kept
  // showing "10% OFF" because the percent_off check ran first. Label
  // now wins; the percent_off is surfaced as the small kicker label.
  const subtitle = coupon.label || (coupon.percent_off ? `${coupon.percent_off}% OFF` : "Coupon");
  const kicker = coupon.percent_off ? `${coupon.percent_off}% OFF` : "Coupon";
  const extra = coupon.promo_code ? (
    <p className="text-center text-sm text-slate-700 px-5 pb-1">
      Online code: <span className="font-mono font-black">{coupon.promo_code}</span>
    </p>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowFull(true)}
        className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white flex items-center justify-center transition-all"
        aria-label="Show coupon"
        title={coupon.percent_off ? `${coupon.percent_off}% off — tap to show` : "Coupon — tap to show"}
        data-testid="coupon-header-pill"
      >
        <Percent weight="bold" className="w-4 h-4" />
      </button>

      {showFull && (
        <BarcodeBottomSheet
          title={kicker}
          subtitle={subtitle}
          value={coupon.barcode_value}
          footer={`Valid through ${shortDate(coupon.valid_until)}`}
          extra={extra}
          onClose={() => setShowFull(false)}
          testId="coupon-fullscreen"
          barcodeTestId="coupon-barcode"
        />
      )}
    </>
  );
}
